import { useMemo } from 'react';
import { css } from '@emotion/react';
import { Icon } from '@frontend/icons';
import { breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { Button, Text } from '@frontend/design-system';
import { UserRole } from '../../utils/constants';
import { checkRbacAccess, formatString } from '../../utils/helpers';
import { IntegrationsMetadata } from '../../utils/typings';

interface PmsVersionsTabProps {
  pmsVersions?: string[];
  integrationsMetadata?: IntegrationsMetadata;
  userRole?: keyof typeof UserRole;
}

const PmsVersionsTab = ({ pmsVersions, integrationsMetadata, userRole }: PmsVersionsTabProps) => {
  const hasViewAccess = useMemo(
    () => checkRbacAccess(integrationsMetadata?.supportedPmsVersions, userRole).hasViewAccess,
    [integrationsMetadata, userRole]
  );

  return (
    <div css={containerStyle}>
      <div css={headerStyle}>
        <Button aria-label='Edit' css={iconButtonStyle}>
          <Icon name='edit' size={20} />
        </Button>
      </div>
      {pmsVersions && pmsVersions.length > 0 && hasViewAccess ? (
        pmsVersions.map((chip) => <Text key={chip}>{formatString(chip)}</Text>)
      ) : (
        <Text>No PMS Versions available.</Text>
      )}
    </div>
  );
};

export default PmsVersionsTab;

const containerStyle = css`
  position: relative;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${theme.spacing(2)};
  align-items: start;
  margin-top: ${theme.spacing(3)};
  border: 1px solid ${theme.colors.neutral[20]};
  border-radius: ${theme.borderRadius.medium};
  padding: ${theme.spacing(3)};

  @media (max-width: ${breakpoints.mobile.min}px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${breakpoints.xsmall.max}px) {
    grid-template-columns: 1fr;
  }
`;

const headerStyle = css`
  position: absolute;
  top: ${theme.spacing(2)};
  right: ${theme.spacing(2)};
  z-index: 1;
`;

const iconButtonStyle = css`
  background: none;
  border: none;
  cursor: pointer;
  padding: ${theme.spacing(0.5)};
  border-radius: ${theme.borderRadius.small};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.black};

  &:hover,
  &:active:not([aria-disabled='true']) {
    background-color: ${theme.colors.neutral[10]};

    & svg {
      color: ${theme.colors.black};
    }
  }
`;
